import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  User, 
  Mail, 
  Phone, 
  Building, 
  MessageSquare,
  CheckCircle,
  Star,
  Zap,
  Gift,
  CreditCard,
  X
} from 'lucide-react';
import PayTRModal from './PayTRModal';
import PaymentSuccessModal from './PaymentSuccessModal';

const ConsultationForm = ({ isModal = false, onClose = null }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '+90 ',
    company_name: '',
    industry: '',
    service_type: '',
    budget_range: '',
    project_description: '',
    timeline: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [showPayTR, setShowPayTR] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [consultationPrice, setConsultationPrice] = useState(500);
  const [paytrToken, setPaytrToken] = useState(null);

  // Fetch consultation price on component mount
  useEffect(() => {
    const fetchConsultationPrice = async () => {
      try {
        const apiBase = process.env.NODE_ENV === 'development' 
          ? 'http://localhost:8000' 
          : `${window.location.protocol}//${window.location.host}`;
          
        const response = await fetch(`${apiBase}/backend/api.php?action=getConsultationPrice`);
        const data = await response.json();
        
        if (data.status === 'success' && data.price) {
          setConsultationPrice(parseFloat(data.price));
        }
      } catch (err) {
        console.error('Failed to fetch consultation price:', err);
      }
    };
    
    fetchConsultationPrice();
  }, []);

  const serviceTypes = [
    { value: 'ecommerce', label: 'E-ticaret Sitesi', icon: '🛒' },
    { value: 'eexport', label: 'E-ihracat Çözümleri', icon: '🌍' }, 
    { value: 'hosting', label: 'Hosting & Domain', icon: '🖥️' },
    { value: 'ai', label: 'Yapay Zeka Çözümleri', icon: '🤖' },
    { value: 'marketing', label: 'Dijital Pazarlama', icon: '📈' },
    { value: 'design', label: 'Tasarım Hizmetleri', icon: '🎨' },
    { value: 'other', label: 'Diğer', icon: '💼' }
  ];

  const budgetRanges = [
    { value: '1000-5000', label: '₺1.000 - ₺5.000' },
    { value: '5000-15000', label: '₺5.000 - ₺15.000' },
    { value: '15000-50000', label: '₺15.000 - ₺50.000' },
    { value: '50000+', label: '₺50.000+' },
    { value: 'not-sure', label: 'Emin Değilim' }
  ];

  const timelines = [
    { value: '1-2weeks', label: '1-2 Hafta' },
    { value: '1month', label: '1 Ay' },
    { value: '2-3months', label: '2-3 Ay' },
    { value: '6months+', label: '6 Ay+' },
    { value: 'flexible', label: 'Esnek' }
  ];

  const formatPhoneNumber = (value) => {
    // +90 kısmını koru ve sadece rakamları al
    const phoneNumber = value.replace('+90 ', '').replace(/\D/g, '');
    
    // Maksimum 10 rakam
    const limited = phoneNumber.slice(0, 10);
    
    // Format: xxx xxx xx xx
    let formatted = '+90 ';
    if (limited.length > 0) {
      formatted += limited.slice(0, 3);
    }
    if (limited.length > 3) {
      formatted += ' ' + limited.slice(3, 6);
    }
    if (limited.length > 6) {
      formatted += ' ' + limited.slice(6, 8);
    }
    if (limited.length > 8) {
      formatted += ' ' + limited.slice(8, 10);
    }
    
    return formatted;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      const formatted = formatPhoneNumber(value);
      setFormData({
        ...formData,
        [name]: formatted
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      // Validate required fields
      if (!formData.full_name || !formData.email || !formData.phone || !formData.service_type) {
        setError('Lütfen gerekli alanları doldurun.');
        setIsSubmitting(false);
        return;
      }
      
      // Initiate PayTR payment
      const apiBase = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:8000' 
        : `${window.location.protocol}//${window.location.host}`;

      const response = await fetch(`${apiBase}/backend/api.php?action=initiatePaytrPayment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      console.log('Response status:', response.status);
      
      const data = await response.json();
      console.log('Response data:', data);

      if (!response.ok) {
        throw new Error(data.message || 'Ödeme başlatılamadı');
      }
      
      if (data.status === 'success' && data.token) {
        setPaytrToken(data.token);
        
        // Order ID'yi localStorage'a kaydet (başarı sayfasında kullanılacak)
        if (data.order_id) {
          localStorage.setItem('current_order_id', data.order_id);
          console.log('✅ Order ID localStorage\'a kaydedildi:', data.order_id);
        }
        
        setShowPayTR(true);
      } else {
        throw new Error(data.message || 'Ödeme token alınamadı');
      }
    } catch (err) {
      console.error('Payment initiation error:', err);
      setError('Ödeme başlatılamadı. Lütfen tekrar deneyin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentSuccess = () => {
    // Önce modal'ı hemen kapat
    setShowPayTR(false);
    
    // Kısa bir gecikme ile ödeme başarı popup'ını göster
    // Bu sayede modal kapanır ve kullanıcı başarı popup'ını görebilir
    setTimeout(() => {
      setShowPaymentSuccess(true);
    }, 300); // 300ms gecikme - modal'ın kapanması için yeterli süre
  };

  const handlePaymentClose = () => {
    setShowPayTR(false);
  };

  if (isSubmitted) {
    return (
      <Card className={`${isModal ? 'w-full' : 'max-w-2xl mx-auto'} bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 shadow-2xl`}>
        <CardContent className="p-10 text-center">
          <div className="space-y-6">
            {/* Success Icon with Animation */}
            <div className="relative">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto animate-bounce">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <div className="absolute inset-0 w-20 h-20 bg-green-200 rounded-full mx-auto animate-ping opacity-20"></div>
            </div>

            {/* Success Title */}
            <div>
              <h3 className="text-3xl font-bold text-green-800 mb-2">
                🎉 Ödemeniz Başarılı!
              </h3>
              <div className="w-24 h-1 bg-green-500 mx-auto rounded-full"></div>
            </div>

            {/* Success Message */}
            <div className="bg-white/50 rounded-lg p-6 space-y-3">
              <p className="text-lg text-green-700 font-medium">
                Ücretli danışmanlık ödemeniz başarıyla alındı.
              </p>
              <p className="text-green-600">
                Toplantı detayları ve randevu bilgileriniz <strong>e-posta ile tarafınıza gönderilecektir</strong>.
              </p>
              <p className="text-green-600 text-sm">
                Uzmanlarımız en kısa sürede sizinle iletişime geçecektir.
              </p>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
              <div className="flex flex-col items-center space-y-2 p-3 bg-white/60 rounded-lg">
                <Star className="w-6 h-6 text-yellow-500" />
                <span className="text-sm text-green-700 font-medium">Öncelikli Hizmet</span>
              </div>
              <div className="flex flex-col items-center space-y-2 p-3 bg-white/60 rounded-lg">
                <Gift className="w-6 h-6 text-purple-500" />
                <span className="text-sm text-green-700 font-medium">Uzman Danışmanlık</span>
              </div>
              <div className="flex flex-col items-center space-y-2 p-3 bg-white/60 rounded-lg">
                <Zap className="w-6 h-6 text-blue-500" />
                <span className="text-sm text-green-700 font-medium">Hızlı Başlangıç</span>
              </div>
            </div>

            {/* Redirect Info */}
            <div className="pt-4 border-t border-green-200">
              <p className="text-sm text-green-600">
                5 saniye içinde ana sayfaya yönlendirileceksiniz...
              </p>
              <div className="mt-3">
                <button
                  onClick={() => window.location.href = '/'}
                  className="text-green-600 hover:text-green-700 underline text-sm font-medium"
                >
                  veya hemen ana sayfaya dönmek için tıklayın →
                </button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={`${isModal ? 'w-full' : 'max-w-2xl mx-auto'} shadow-xl border-0`}>
        <CardHeader className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-t-lg relative">
          {isModal && onClose && (
            <Button
              onClick={onClose}
              variant="ghost"
              size="icon"
              className="absolute top-3 right-3 sm:top-4 sm:right-4 z-[100] bg-white/20 hover:bg-white/30 text-white w-10 h-10 sm:w-11 sm:h-11 p-0 rounded-full shadow-lg border-2 border-white/30"
              aria-label="Kapat"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </Button>
          )}
          <div className={`flex items-center justify-between ${isModal && onClose ? 'sm:pr-16' : ''}`}>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <Zap className="w-6 h-6" />
              </div>
              <div>
                <CardTitle className="text-2xl">Ücretli Danışmanlık</CardTitle>
                <p className="text-purple-100 mt-1">Uzmanlarımızla ücretli danışmanlık alın</p>
              </div>
            </div>
          </div>
          
          {/* Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            {[
              { icon: Star, text: "Uzman Analiz" },
              { icon: Gift, text: "Öncelikli Randevu" },
              { icon: Zap, text: "Hızlı Başlangıç" }
            ].map((benefit, index) => (
              <div key={index} className="flex items-center space-x-2 text-purple-100">
                <benefit.icon className="w-4 h-4" />
                <span className="text-sm">{benefit.text}</span>
              </div>
            ))}
          </div>
        </CardHeader>

        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="full_name" className="flex items-center space-x-2">
                  <User className="w-4 h-4" />
                  <span>Ad Soyad *</span>
                </Label>
                <Input
                  id="full_name"
                  name="full_name"
                  type="text"
                  placeholder="Adınız Soyadınız"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  className="border-gray-300 focus:border-purple-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <span>E-posta *</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="ornek@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
                  title="Geçerli bir e-posta adresi girin"
                  className="border-gray-300 focus:border-purple-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <span>Telefon *</span>
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+90 555 123 45 67"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  title="Telefon numaranızı girin"
                  className="border-gray-300 focus:border-purple-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_name" className="flex items-center space-x-2">
                  <Building className="w-4 h-4" />
                  <span>Şirket Adı</span>
                </Label>
                <Input
                  id="company_name"
                  name="company_name"
                  type="text"
                  placeholder="Şirket adınız (opsiyonel)"
                  value={formData.company_name}
                  onChange={handleChange}
                  className="border-gray-300 focus:border-purple-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="industry" className="flex items-center space-x-2">
                  <span>Sektör</span>
                </Label>
                <Input
                  id="industry"
                  name="industry"
                  type="text"
                  placeholder="Örn: E-ticaret, Sağlık, Eğitim"
                  value={formData.industry}
                  onChange={handleChange}
                  className="border-gray-300 focus:border-purple-500"
                />
              </div>
            </div>

            {/* Service Selection */}
            <div className="space-y-2">
              <Label htmlFor="service_type">Hangi hizmetle ilgileniyorsunuz? *</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {serviceTypes.map((service) => (
                  <label
                    key={service.value}
                    className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-all hover:border-purple-500 ${
                      formData.service_type === service.value
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <input
                      type="radio"
                      name="service_type"
                      value={service.value}
                      checked={formData.service_type === service.value}
                      onChange={handleChange}
                      className="hidden"
                    />
                    <span className="text-xl">{service.icon}</span>
                    <span className="text-sm font-medium">{service.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Budget & Timeline */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="budget_range">Bütçe Aralığı</Label>
                <select
                  id="budget_range"
                  name="budget_range"
                  value={formData.budget_range}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="">Bütçe seçin</option>
                  {budgetRanges.map((budget) => (
                    <option key={budget.value} value={budget.value}>
                      {budget.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="timeline">Proje Süresi</Label>
                <select
                  id="timeline"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="">Süre seçin</option>
                  {timelines.map((timeline) => (
                    <option key={timeline.value} value={timeline.value}>
                      {timeline.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Project Description */}
            <div className="space-y-2">
              <Label htmlFor="project_description" className="flex items-center space-x-2">
                <MessageSquare className="w-4 h-4" />
                <span>Proje Detayları</span>
              </Label>
              <textarea
                id="project_description"
                name="project_description"
                rows="4"
                placeholder="Projeniz hakkında detayları paylaşın. Ne yapmak istiyorsunuz? Hedefleriniz neler?"
                value={formData.project_description}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
              />
            </div>

            {error && (
              <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded-lg">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-medium py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                  <span>İşleniyor...</span>
                </div>
              ) : (
                <>
                  <CreditCard className="w-5 h-5 mr-2 inline" />
                  {consultationPrice}₺ ile Ödeme Yap
                </>
              )}
            </Button>

            {/* Trust Indicators */}
            <div className="flex items-center justify-center space-x-6 text-sm text-gray-600 pt-4 border-t">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Öncelikli Görüşme</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>24 Saat Yanıt</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Uzman Danışmanlık</span>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* PayTR Modal */}
      {showPayTR && paytrToken && (
        <PayTRModal
          token={paytrToken}
          onClose={handlePaymentClose}
          onSuccess={handlePaymentSuccess}
        />
      )}

      {/* Payment Success Modal */}
      <PaymentSuccessModal
        isOpen={showPaymentSuccess}
        onClose={() => setShowPaymentSuccess(false)}
      />
    </>
  );
};

export default ConsultationForm;
